import { ItemStatusBadge } from '@/components/badges/ItemStatusBadge'
import { BaseDialog } from '@/components/BaseDialog'
import { ImageUpload } from '@/components/ImageUpload'
import { OnlineStatusDot } from '@/components/ItemOnlineStatus'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ScanItemContent } from '@/features/scans/components/ScanItemContent'
import { useDateFormatter } from '@/hooks/useDateFormatter'
import { formatBuildingFloorLocation } from '@/utils/location'
import { Building2, Calendar, Hash, Image, MapPin, Package, PencilLine, Shield, Store, Tag, Wrench } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useGetItem } from '../hooks/useItem'

export function ItemDetailDialog({
  open,
  onOpenChange,
  itemId,
}: {
  open: boolean
  onOpenChange: (o: boolean) => void
  itemId: string | null
}) {
  const { t } = useTranslation()
  const { formatDateTime } = useDateFormatter()
  const { data: item, isLoading: isLoadingItem } = useGetItem(itemId!, !!itemId && open)

  if (!item) return null
  const product = item.product
  const department = item.department
  const vendor = item.vendor

  const details = [
    { icon: Hash, label: t('equipment.item.serialNumber'), value: item.serialNumber },
    { icon: Tag, label: t('equipment.model.model'), value: `${product?.brand} ${product?.model}` || '-' },
    { icon: Building2, label: t('common.department'), value: department?.name || '-' },
    {
      icon: MapPin,
      label: t('equipment.item.location'),
      value: [formatBuildingFloorLocation(t, department?.buildingName, department?.floorName)]
        .filter(Boolean)
        .join(' / '),
    },
    { icon: Store, label: t('common.vendor'), value: vendor?.name || '-' },
    {
      icon: Calendar,
      label: t('equipment.item.purchaseDate'),
      value:
        formatDateTime(item.purchaseDate, {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        }) || '-',
    },
    {
      icon: Shield,
      label: t('equipment.item.warrantyExpire'),
      value:
        formatDateTime(item.warrantyExpiry, {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        }) || '-',
    },
    {
      icon: Wrench,
      label: t('equipment.item.nextMa'),
      value:
        formatDateTime(item.nextMaintenance, {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        }) || '-',
    },
    { icon: Package, label: t('equipment.item.assignTo'), value: item.assignedTo?.name || '-' },
    { icon: Hash, label: t('equipment.item.lotId'), value: item.lotId || '-' },
    { icon: PencilLine, label: t('equipment.item.note'), value: item.notes || '-' },
  ]

  return (
    <>
      <BaseDialog
        open={open}
        onOpenChange={onOpenChange}
        title={product?.name ?? t('common.equipment')}
        header={
          <>
            <OnlineStatusDot
              supportsOnlineStatus={product?.supportsOnlineStatus ?? false}
              onlineStatus={item.onlineStatus}
            />
            <span>{product?.name || 'อุปกรณ์'}</span>
            <ItemStatusBadge status={item.status} />
          </>
        }
        contentTestId="item-detail-dialog"
      >
        {isLoadingItem ? (
          <></>
        ) : (
          <>
            <Tabs defaultValue="info" className="w-full">
              <TabsList className="w-full">
                <TabsTrigger value="info" className="flex-1 text-xs" disabled={isLoadingItem} data-testid="item-detail-tab-info">
                  {t('common.info')}
                </TabsTrigger>
                <TabsTrigger value="management" className="flex-1 text-xs" disabled={isLoadingItem} data-testid="item-detail-tab-management">
                  {t('common.management')}
                </TabsTrigger>
              </TabsList>
              <TabsContent value="info" className="mt-4">
                <div className="flex flex-col gap-3">
                  {details.map(d => (
                    <div
                      key={d.label}
                      className="flex items-center gap-3 rounded-lg border border-border/30 bg-secondary/30 p-3"
                    >
                      <d.icon className="size-4 shrink-0 text-primary" />
                      <div className="flex flex-col gap-0.5">
                        <span className="text-[10px] text-muted-foreground">{d.label}</span>
                        <span className="text-xs font-medium text-foreground">{d.value}</span>
                      </div>
                    </div>
                  ))}
                  <div className="flex items-center gap-3 rounded-lg border border-border/30 bg-secondary/30 p-3">
                    <Image className="size-4 shrink-0 text-primary" />
                    <div className="flex flex-col gap-0.5">
                      <span className="text-[10px] text-muted-foreground">{t('common.images')}</span>

                      {item.imageIds.length > 0 ? (
                        <ImageUpload
                          files={[]}
                          imageIds={item.imageIds}
                          onChange={() => {}}
                          onRemoveExisting={() => {}}
                          maxImages={5}
                          readonly
                        />
                      ) : (
                        <span className="text-xs font-medium text-foreground">-</span>
                      )}
                    </div>

                    <div className="mt-2"></div>
                  </div>
                </div>
              </TabsContent>
              <TabsContent value="management" className="mt-4">
                <ScanItemContent itemId={item.id}></ScanItemContent>
              </TabsContent>
            </Tabs>
          </>
        )}
      </BaseDialog>
    </>
  )
}
